import { api, csrfHeader } from '../api.js';
import { el, fragment } from '../dom.js';
import { t } from '../i18n.js';
import { openMembersForm } from './members-form.js';
import { institutionScope, institutionHead, NoAccessError, accessDenied } from './institution-scope.js';

// Identity, picture, collaborators, export. These were three buttons opening
// three dialogs off a venue screen; they are one page because they are one
// subject, and because a dialog is the wrong container for settings you read
// as often as you change.
//
// The slug is in the same form as the rest but confirmed separately: renaming
// it rewrites the bio link every visitor already has. Old slugs keep
// redirecting forever, and a rename is still a decision, not a field you tab
// past.
export async function renderAccount({ params }) {
  let scope;
  try {
    scope = await institutionScope(params[0]);
  } catch (error) {
    if (!(error instanceof NoAccessError)) throw error;
    return accessDenied();
  }

  const account = await api.get(`/accounts/${scope.account.id}`);
  const bar = institutionHead(scope, 'account');

  const exportLink = el('p', { class: 'card-meta' }, [
    el('a', { href: `/panel/api/accounts/${account.id}/export`, download: `${account.slug}.json` }, t.exportData),
    ` — ${t.exportHint}`,
  ]);

  if (!account.canManage) {
    return fragment([bar, el('p', { class: 'card-meta' }, t.ownerOnly), exportLink]);
  }

  const cities = await api.get('/cities');

  const name = text({ id: 'af-name', required: true, value: account.name });
  const street = text({ id: 'af-address', value: account.address });
  const website = el('input', { id: 'af-website', type: 'url', value: account.website ?? '' });
  const description = el('textarea', { id: 'af-description', rows: 3 });
  description.value = account.description ?? '';

  const city = el('select', { id: 'af-city' },
    cities.map((row) => el('option', { value: row.id }, row.name)));
  city.value = account.cityId;

  const slug = text({ id: 'af-slug', value: account.slug });
  const picture = profilePicture(account);

  const error = el('p', { class: 'pub-error', role: 'alert' });
  error.hidden = true;

  const status = el('p', { class: 'cal-hint', role: 'status' });
  status.hidden = true;

  const submit = el('button', { class: 'button', type: 'submit' }, t.save);
  const team = el('button', { class: 'button button-quiet', type: 'button' }, t.team);
  team.addEventListener('click', () => openMembersForm({ venue: account, onSaved: () => {} }));

  const form = el('form', { class: 'form form-wide' }, [
    error,
    status,
    picture.node,
    field('af-name', t.fieldVenueName, name),
    field('af-city', t.fieldCity, city),
    field('af-address', t.fieldAddress, street),
    field('af-website', t.fieldWebsite, website),
    field('af-description', t.fieldDescription, description),
    field('af-slug', t.fieldSlug, slug),
    el('p', { class: 'card-meta' }, t.slugWarning),
    el('div', { class: 'row' }, [submit, team]),
  ]);

  form.addEventListener('submit', async (event) => {
    event.preventDefault();
    if (!name.value.trim()) return fail(t.venueNameRequired);

    const renaming = slug.value.trim() !== account.slug;
    if (renaming && !window.confirm(t.confirmRename)) return;

    submit.disabled = true;
    try {
      await api.patch(`/accounts/${account.id}`, {
        name: name.value.trim(),
        cityId: Number(city.value),
        address: street.value.trim() || null,
        website: website.value.trim() || null,
        description: description.value.trim() || null,
        slug: renaming ? slug.value.trim() : undefined,
      });
      error.hidden = true;
      status.textContent = t.saved;
      status.hidden = false;
      // A rename changes the address shown above it and the one in the switcher,
      // so the page has to reflect what was just written rather than what it
      // was loaded with.
      // The nickname is in this page's own address, so a rename moves the page.
      if (renaming) window.location.assign(`/panel/i/${slug.value.trim()}/konto`);
    } catch (error_) {
      fail(error_.code === 'bad_slug' ? t.slugRejected : t.saveFailed);
    } finally {
      submit.disabled = false;
    }
  });

  function fail(message) {
    status.hidden = true;
    error.textContent = message;
    error.hidden = false;
  }

  return fragment([bar, form, exportLink]);
}

function text(attrs) {
  const node = el('input', { type: 'text', autocomplete: 'off', ...attrs, value: undefined });
  node.value = attrs.value ?? '';
  return node;
}

function field(id, label, control) {
  return el('div', { class: 'field' }, [el('label', { for: id }, label), control]);
}

// One picture, uploaded on pick rather than on save. It is a separate route
// for a reason — a name edit must not be able to drop it — so making the form
// carry it to the submit handler would reintroduce exactly that coupling in
// the client. What Save writes is text; the picture is already written by then.
function profilePicture(account) {
  // Square, unconditionally. This form is only ever reached at
  // /panel/i/<nickname>/konto, so what it previews is always an institution —
  // and round would show the owner a shape the picture will never have
  // anywhere the picture actually appears. A preview that lies about the
  // result is worse than no preview.
  const preview = el('img', { class: 'avatar avatar-lg avatar-square', alt: '' });
  const mono = el('span', { class: 'avatar avatar-lg avatar-square avatar-mono', 'aria-hidden': 'true' },
    (account.name ?? '?').trim().slice(0, 1).toUpperCase());

  const picker = el('input', { type: 'file', accept: 'image/jpeg,image/png,image/webp,image/avif' });
  picker.hidden = true;

  const choose = el('button', { class: 'button button-quiet', type: 'button' }, t.addImage);
  const drop = el('button', { class: 'button button-quiet', type: 'button' }, t.removeImage);
  const status = el('p', { class: 'card-meta' }, t.imagesStripped);

  show(account.imageUrl ?? null);

  choose.addEventListener('click', () => picker.click());
  picker.addEventListener('change', () => {
    const file = picker.files?.[0];
    if (file) send('PUT', file);
    picker.value = '';
  });
  drop.addEventListener('click', () => send('DELETE', null));

  const node = el('div', { class: 'field' }, [
    el('label', {}, t.fieldProfileImage),
    el('div', { class: 'row row-tight' }, [preview, mono, choose, drop, picker]),
    status,
  ]);

  return { node };

  function show(url) {
    preview.hidden = !url;
    mono.hidden = Boolean(url);
    drop.hidden = !url;
    if (url) preview.src = url;
  }

  async function send(method, file) {
    choose.disabled = true;
    drop.disabled = true;
    status.textContent = t.uploading;
    try {
      // Raw body, not multipart: parsing multipart server-side would be a
      // fifth runtime dependency for one endpoint, and fetch(file) already
      // sends the bytes with the file's own Content-Type.
      const response = await fetch(`/panel/api/accounts/${account.id}/image`, {
        method,
        headers: { ...csrfHeader(), ...(file ? { 'Content-Type': file.type } : {}) },
        body: file,
      });
      if (!response.ok) throw new Error(String(response.status));
      const updated = await response.json();
      account.imageUrl = updated.imageUrl;
      show(updated.imageUrl);
      status.textContent = t.imagesStripped;
    } catch {
      status.textContent = t.uploadFailed;
    } finally {
      choose.disabled = false;
      drop.disabled = false;
    }
  }
}
